import React, { useState, useRef, useEffect, createContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './AppLayout.css';
import logo from '../assets/sportsbuff.png';
import tempAvatar from '../assets/tempAvatar.png';
import { onAuthStateChanged } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import { auth, db, storage } from '../firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { streakDate } from '../streak';
import { ref, getDownloadURL } from 'firebase/storage';


import {
  Home09Icon,
  ChampionIcon,
  Calendar03Icon,
  RankingIcon,
  IdeaIcon,
  Menu01Icon,
  LogoutCircle01Icon,
  LoginCircle01Icon,
  InformationCircleIcon,
  Notification02Icon,
  Mail01Icon,
  SquareLock02Icon,
  HelpCircleIcon,
  Edit02Icon,
  UserEdit01Icon,
  UserAdd01Icon
} from 'hugeicons-react';

export const AppUserContext = createContext({
  headerAvatarUrl: null,
  currentUser: null,
});

function AppLayout({ children }) {
  
  const location = useLocation();
  const hideHeader =
  location.pathname === "/Terms" ||
  location.pathname === "/Privacy" ||
  location.pathname === "/news/faqs/p5Idyk6hS99xRXTCV5Gv";
  const showFooter =
  location.pathname === "/Games" ||
  location.pathname === "/Leagues" ||
  location.pathname === "/Predictor" ||
  location.pathname === "/Calendar";

  const showFab = false;
  //location.pathname.startsWith("/Games") ||
  //location.pathname.startsWith("/Leagues") ||
  //location.pathname.startsWith("/Predictor") ||
  //location.pathname.startsWith("/Calendar");

  const navigate = useNavigate();
  const mainRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const isRealUser = !!currentUser && !currentUser.isAnonymous;
  const [streakCount, setStreakCount] = useState(0);
  const [lastStreakDate, setLastStreakDate] = useState(null); // "YYYY-MM-DD"
  const [streakTz, setStreakTz] = useState('Europe/London');
  const [streakCutoffHour, setStreakCutoffHour] = useState(0);
  const assetBase = process.env.PUBLIC_URL || '';
  const [headerAvatarUrl, setHeaderAvatarUrl] = useState(tempAvatar);






const handleLogout = () => {
  signOut(auth)
    .then(() => navigate('/'))
    .catch((error) => console.error('Logout error:', error));
};

const NOTIFICATION_VERSION = 1; // bump to 2, 3, 4 whenever you want it to show again
const LS_READ_KEY = 'sb_read_notification_version';

const [hideReminder, setHideReminder] = useState(false);

const handleHideReminder = async () => {
  try {
    // open in new tab (same behaviour as menu)
    window.open("/news/faqs/p5Idyk6hS99xRXTCV5Gv", "_blank", "noopener,noreferrer");

    if (isRealUser) {
      const ref = doc(db, 'users', currentUser.uid);
      await setDoc(ref, { readNotificationVersion: NOTIFICATION_VERSION }, { merge: true });
    }

    localStorage.setItem(LS_READ_KEY, String(NOTIFICATION_VERSION));
    setHideReminder(true);
  } catch (err) {
    console.error('Failed to set readNotification:', err);
  }
};

  const navItems = [
    { icon: <Calendar03Icon size={40} strokeWidth={2} />, label: 'Calendar', route: '/Calendar' },
    { icon: <ChampionIcon size={40} strokeWidth={2} />, label: 'Games', route: '/Games' },
    { icon: <IdeaIcon size={40} strokeWidth={2} />, label: 'Predictor', route: '/Predictor' },
    { icon: <RankingIcon size={40} strokeWidth={2} />, label: 'Leagues', route: '/Leagues' },
  ];

  // --- NEW: floating store icon state/animation toggle
  const [showApple, setShowApple] = useState(true);
  const [wiggle, setWiggle] = useState(false);

useEffect(() => {
  // If we just came from the auto-scrolling League page, or on any mount,
  // ensure the app scroller is at the top after layout settles.
  if (sessionStorage.getItem('fromLeague')) {
    sessionStorage.removeItem('fromLeague');
  }
  const scroller = document.querySelector('.app-main');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => scroller?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' }));
  });
}, [location.pathname]);
  

useEffect(() => {
  let unsubUserDoc = null;

const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
  setCurrentUser(user || null);

  const seenVer = Number(localStorage.getItem(LS_READ_KEY) || "0");
  setHideReminder(seenVer >= NOTIFICATION_VERSION);

  // Treat anonymous like signed-out for layout purposes
  if (!user || user.isAnonymous) {
    setHeaderAvatarUrl(tempAvatar);

    if (unsubUserDoc) unsubUserDoc();
    unsubUserDoc = null;

    // optional: reset streak UI state too
    setStreakCount(0);
    setLastStreakDate(null);

    return;
  }

    // Signed in: live-listen to user doc changes (avatarThumbPath updates instantly)
    const userRef = doc(db, "users", user.uid);

    if (unsubUserDoc) unsubUserDoc(); // safety if auth re-fires
    unsubUserDoc = onSnapshot(
      userRef,
      async (snap) => {
        const data = snap.exists() ? snap.data() : {};

        // --- header avatar (thumb) ---
        try {
          const DEFAULT_THUMB_PATH = "avatars/v1/defaults/thumb.png";
          const thumbPath = data.avatarThumbPath || DEFAULT_THUMB_PATH;
          const url = await getDownloadURL(ref(storage, thumbPath));
          setHeaderAvatarUrl(url);
        } catch (e) {
          console.error("Failed to load header avatar thumb:", e);
          setHeaderAvatarUrl(tempAvatar);
        }

        // --- notifications: Firestore wins when signed in ---
        const fsSeenVer = Number(data.readNotificationVersion || 0);
        setHideReminder(fsSeenVer >= NOTIFICATION_VERSION);

        // --- read streak fields for the header pill ---
        if (typeof data.streak_count === "number") setStreakCount(data.streak_count);
        if (typeof data.last_streak_date === "string") setLastStreakDate(data.last_streak_date);
        if (typeof data.streak_timezone === "string") setStreakTz(data.streak_timezone);
        if (typeof data.streak_cutoff_hour === "number") setStreakCutoffHour(data.streak_cutoff_hour);
      },
      (err) => {
        console.error("User doc snapshot error:", err);
      }
    );
  });

  return () => {
    if (unsubUserDoc) unsubUserDoc();
    unsubscribeAuth();
  };
}, []);


useEffect(() => {
  const t = setInterval(() => {
    setShowApple((s) => !s);
  }, 6000);
  return () => clearInterval(t);
}, []);

useEffect(() => {
  // retrigger wiggle each time icon changes
  setWiggle(true);
  const timeout = setTimeout(() => setWiggle(false), 1200); // match animation duration
  return () => clearTimeout(timeout);
}, [showApple]);
  
 // compute "today" first, then "yesterday", then derive the number to show
 const todayStreakDate = streakDate(new Date(), streakTz, streakCutoffHour);
 const yesterdayStreakDate = streakDate(
   new Date(Date.now() - 24 * 60 * 60 * 1000),
   streakTz,
   streakCutoffHour
 );
 // show 0 if the user missed a day (i.e., last_streak_date is neither today nor yesterday)
 const displayStreak =
   (lastStreakDate === todayStreakDate || lastStreakDate === yesterdayStreakDate)
     ? (streakCount ?? 0)
     : 0;
  const hasKeptStreakToday = lastStreakDate === todayStreakDate;
  const streakTextColor = hasKeptStreakToday ? '#B3FF66' : '#38464E';
  const streakIconSrc   = hasKeptStreakToday
  ? `${assetBase}/icons/streakok.png`
  : `${assetBase}/icons/streak.png`;

// ✅ Used to measure where the streak pill sits on screen
const streakPillRef = useRef(null);

// ✅ Where the lightning should start (screen coords)
const [streakOrigin, setStreakOrigin] = useState({ x: 18, y: 58 });

const [streakFlash, setStreakFlash] = useState(false);
const prevKeptRef = useRef(false);
const streakArmedRef = useRef(false);
const [boltPaths, setBoltPaths] = useState({ main: "", b1: "", b2: "" });

const rumbleStreak = () => {
  // Vibration API: supported mainly on Android Chrome
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    // short "thunder" pattern: vibrate, pause, vibrate
navigator.vibrate([
  90, 35,
  140, 45,
  160
]);
  }
};

useEffect(() => {
  // If signed out, reset the arming so we don't get weird flashes later
  if (!currentUser) {
    streakArmedRef.current = false;
    prevKeptRef.current = false;
    return;
  }

  // IMPORTANT: wait until Firestore has actually provided streak data.
  // lastStreakDate starts as null on page load; we don't want false->true to count.
  const streakDataLoaded = typeof lastStreakDate === "string";

  if (!streakDataLoaded) {
    return; // don't arm, don't animate
  }

  // First time AFTER data is loaded: arm baseline, no animation
  if (!streakArmedRef.current) {
    prevKeptRef.current = hasKeptStreakToday;
    streakArmedRef.current = true;
    return;
  }

  const prev = prevKeptRef.current;

  // Only flash on a real flip false -> true after we're armed
  if (!prev && hasKeptStreakToday) {
    setStreakFlash(true);
    setTimeout(rumbleStreak, 60);
    const t = setTimeout(() => setStreakFlash(false), 900);
    return () => clearTimeout(t);
  }

  prevKeptRef.current = hasKeptStreakToday;
}, [currentUser, lastStreakDate, hasKeptStreakToday]);


useEffect(() => {
  if (!streakFlash) return;

  const el = streakPillRef.current;
  if (!el) return;

  const r = el.getBoundingClientRect();

  // Start near the lightning icon (a little inside the pill)
  const x = Math.round(r.left + 14);       // tweak 14 if needed
  const y = Math.round(r.top + r.height / 2);

  setStreakOrigin({ x, y });
}, [streakFlash]);

useEffect(() => {
  if (!streakFlash) return;

  const w = window.innerWidth;
  const h = window.innerHeight;

  const x0 = streakOrigin.x;
  const y0 = streakOrigin.y;

  // Helper: clamp so forks don’t go off-screen
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  // Build a sharp zig-zag main bolt
  const points = [];
  points.push([x0, y0]);

  const steps = 9; // more steps = more zigzags
  const dy = (h - y0) / steps;

  let x = x0;
  let y = y0;

  for (let i = 1; i <= steps; i++) {
    y = y0 + dy * i;

    // alternate left/right, with some randomness
    const dir = i % 2 === 0 ? 1 : -1;
    const jitter = (Math.random() * 40 + 25) * dir; // 25–65px
    x = clamp(x0 + jitter, 12, w - 12);

    points.push([x, y]);
  }

  const toPath = (pts) =>
    pts.map((p, idx) => (idx === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(" ");

  const main = toPath(points);

  // Fork 1: peel off around 35–55% down
  const forkStart1 = points[Math.floor(steps * 0.4)];
  const fork1 = [
    forkStart1,
    [clamp(forkStart1[0] + 90, 12, w - 12), forkStart1[1] + dy * 1.2],
    [clamp(forkStart1[0] + 50, 12, w - 12), forkStart1[1] + dy * 2.1],
  ];

  // Fork 2: peel off around 55–75% down
  const forkStart2 = points[Math.floor(steps * 0.65)];
  const fork2 = [
    forkStart2,
    [clamp(forkStart2[0] - 85, 12, w - 12), forkStart2[1] + dy * 1.0],
    [clamp(forkStart2[0] - 35, 12, w - 12), forkStart2[1] + dy * 2.0],
  ];

  setBoltPaths({
    main,
    b1: toPath(fork1),
    b2: toPath(fork2),
  });
}, [streakFlash, streakOrigin]);




  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    // use instant jump to avoid visible jank; 'auto' is fine too
    el.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);  

  return (
    <AppUserContext.Provider value={{ headerAvatarUrl, currentUser }}>
    <div className="app-container">
{streakFlash && (
  <div className="streak-lightning-overlay" aria-hidden="true">
    <svg className="streak-lightning-svg" width="100%" height="100%">
      <path className="bolt bolt--main" d={boltPaths.main} />
      <path className="bolt bolt--branch" d={boltPaths.b1} />
      <path className="bolt bolt--branch" d={boltPaths.b2} />
    </svg>
  </div>
)}
      {!hideHeader && (
      <header className="app-header">
        
        <div className="header-left">
          <img src={headerAvatarUrl} alt="avatar" className="header-avatar" />
          <img src={logo} 
          alt="logo" 
          className="header-logo"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/Games")} />
  {isRealUser && (
    <div
      ref={streakPillRef}
      className={`streak-pill ${streakFlash ? "streak-pill--flash" : ""}`}
      title={hasKeptStreakToday ? 'Streak kept today' : 'Keep your streak today'}
    >
      <img
        src={streakIconSrc}
        alt={hasKeptStreakToday ? 'Daily streak kept' : 'Daily streak not yet kept'}
        className="streak-icon"
        width="18"
        height="18"
        loading="eager"
        decoding="async"
      />
      <span className="streak-count" style={{ color: streakTextColor }}>
        {displayStreak}
      </span>
    </div>
  )}          
        </div>

        <div className="header-menu">
          <Menu01Icon
            size={24}
            style={{ cursor: 'pointer', color: 'white' }}
            onClick={() => setMenuOpen(!menuOpen)}
          />
<>
  <div className={`sidebar-menu ${menuOpen ? 'open' : ''}`}>
    <div className="sidebar-header">
      <h2>Menu</h2>
      <Menu01Icon size={24} style={{ cursor: 'pointer' }} onClick={() => setMenuOpen(false)} />
    </div>

{isRealUser && (
  <div className="sidebar-user-info">
    <div className="user-name">{currentUser.displayName}</div>
    <div className="user-email">{currentUser.email}</div>
  </div>
)}

    <div className="sidebar-items">
      {isRealUser && (
      <div onClick={() => { navigate('/Games'); setMenuOpen(false); }}>
        <Home09Icon size={24} /> Home
      </div>
      )}
      {!isRealUser && (
      <div onClick={() => { navigate('/sports'); setMenuOpen(false); }}>
        <UserAdd01Icon size={24} /> Sign Up
      </div>
      )}      
      <div onClick={() => { window.location.href = "mailto:contact@sportsbuff.co.uk"; setMenuOpen(false); }}>
        <Mail01Icon size={24} /> Contact Us
      </div>
      {isRealUser && (
      <div onClick={() => { navigate('/Sports'); setMenuOpen(false); }}>
        <Edit02Icon size={24} /> Edit Sports
      </div>  
      )}   
      {isRealUser && (
      <div onClick={() => { navigate('/avatar'); setMenuOpen(false); }}>
        <UserEdit01Icon size={24} /> Edit Avatar
      </div>
      )}
      <div onClick={() => { window.open("/news/faqs/p5Idyk6hS99xRXTCV5Gv", "_blank", "noopener,noreferrer"); setMenuOpen(false); }}>
        <HelpCircleIcon size={24} /> FAQs
      </div>
      {isRealUser && (
      <div onClick={() => { navigate('/Notifications'); setMenuOpen(false); }}>
        <Notification02Icon size={24} /> Notifications
      </div>
      )}
      <div onClick={() => { window.open("/Terms", "_blank", "noopener,noreferrer"); setMenuOpen(false); }}>
        <InformationCircleIcon size={24} /> Terms of Service
      </div>
      <div onClick={() => { window.open("/Privacy", "_blank", "noopener,noreferrer"); setMenuOpen(false); }}>
        <SquareLock02Icon size={24} /> Privacy Policy
      </div>
      {isRealUser && (
      <div onClick={() => { handleLogout(); setMenuOpen(false); }}>
        <LogoutCircle01Icon size={24} /> Log Out
      </div> 
      )}
      {!isRealUser && (
      <div onClick={() => { navigate('/signin'); setMenuOpen(false); }}>
        <LoginCircle01Icon size={24} /> Sign In
      </div>
      )}

    </div>
    <div className="sidebar-footer">
  © sportsbuff 2026. All rights reserved.
    </div>
  </div>

  {menuOpen && <div className="backdrop" onClick={() => setMenuOpen(false)} />}
</>
        </div>
      </header>
      )}
      <main className="app-main" ref={mainRef}>{children}</main>

      {/* --- NEW: floating store button (with custom images) */}
      {showFab && !menuOpen && !hideReminder && (
      <button
      type="button"
      className={`store-fab ${showApple ? 'apple' : 'google'} ${wiggle ? 'wiggle' : ''}`}
      aria-label="Get the app"
      onClick={handleHideReminder}
      >
      <img
      /*src={showApple ? "/icons/appstore.png" : "/icons/googlestore.png"}*/
      src={showApple ? "/icons/premium.png" : "/icons/premium.png"}
      alt={showApple ? "Download on the App Store" : "Get it on Google Play"}
      className="store-img"
      />
      </button>
      )}

{showFooter && (
      <footer className="app-footer">
        {navItems.map((item) => (
          <div
            key={item.route}
            className={`footer-icon ${location.pathname === item.route ? 'active' : ''}`}
            onClick={() => navigate(item.route)}
          >
            {item.icon}
            <div className="footer-label">{item.label}</div>
          </div>
        ))}
      </footer>
)}
    </div>
    </AppUserContext.Provider>
  );
}

export default AppLayout;
